import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Copy, Loader2, ExternalLink, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminResourceManager from "@/components/AdminResourceManager";
import type { User, Session } from "@supabase/supabase-js";

const AdminPanel = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testName, setTestName] = useState("");
  const [generatedToken, setGeneratedToken] = useState<{
    token: string;
    url: string;
    assessmentId: string;
  } | null>(null);

  // Check authentication and admin status
  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (!session) {
          navigate("/auth");
        } else {
          // Check admin status after session is set
          setTimeout(() => {
            checkAdminStatus(session.user.id);
          }, 0);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (!session) {
        navigate("/auth");
        setIsCheckingAuth(false);
      } else {
        checkAdminStatus(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const checkAdminStatus = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .eq('role', 'admin')
        .maybeSingle();

      setIsAdmin(!!data);
      
      if (!data) {
        toast({
          title: "Access Denied",
          description: "You need admin privileges to access this page.",
          variant: "destructive",
        });
        navigate("/");
      }
    } catch (error) {
      console.error("Error checking admin status:", error);
      navigate("/");
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  const generateTestToken = async () => {
    setIsLoading(true);
    try {
      if (!session) {
        toast({
          title: "Authentication Required",
          description: "Please log in to generate test tokens",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke('generate-test-token', {
        body: {
          email: testEmail || undefined,
          name: testName || undefined,
        },
      });

      if (error) throw error;

      if (!data.success) {
        throw new Error(data.error || 'Failed to generate token');
      }

      setGeneratedToken({
        token: data.accessToken,
        url: data.accessUrl,
        assessmentId: data.assessmentId,
      });

      toast({
        title: "Test Token Generated! 🎉",
        description: "You can now use this token to test the assessment flow",
      });

    } catch (error) {
      console.error('Error generating test token:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to generate test token",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      description: `${label} copied to clipboard`,
    });
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 py-12 px-4 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" />
          <h2 className="text-2xl font-bold">Checking authorization...</h2>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold mb-2">Admin Panel</h1>
            <p className="text-muted-foreground">
              Generate test assessment tokens without payment
            </p>
            {user && (
              <p className="text-sm text-muted-foreground mt-2">
                Logged in as: {user.email}
              </p>
            )}
          </div>
          <Button variant="outline" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        <Card className="p-8 shadow-lg mb-8">
          <h2 className="text-2xl font-bold mb-6">Generate Test Token</h2>
          
          <div className="space-y-4 mb-6">
            <div className="space-y-2">
              <Label htmlFor="test-email">Test Email (optional)</Label>
              <Input
                id="test-email"
                type="email"
                placeholder="test@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                Leave blank to use your account email
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="test-name">Test Name (optional)</Label>
              <Input
                id="test-name"
                type="text"
                placeholder="Test User"
                value={testName}
                onChange={(e) => setTestName(e.target.value)}
              />
            </div>
          </div>

          <Button
            onClick={generateTestToken}
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              "Generate Test Token"
            )}
          </Button>
        </Card>

        {generatedToken && (
          <Card className="p-8 shadow-lg border-primary/20">
            <h3 className="text-xl font-bold mb-4 text-primary">
              ✅ Test Token Generated
            </h3>

            <div className="space-y-4">
              <div>
                <Label className="text-sm text-muted-foreground mb-1 block">
                  Assessment ID
                </Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-3 bg-muted rounded text-sm font-mono overflow-auto">
                    {generatedToken.assessmentId}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(generatedToken.assessmentId, "Assessment ID")}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-sm text-muted-foreground mb-1 block">
                  Access Token
                </Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-3 bg-muted rounded text-sm font-mono overflow-auto">
                    {generatedToken.token}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(generatedToken.token, "Token")}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-sm text-muted-foreground mb-1 block">
                  Assessment URL
                </Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-3 bg-muted rounded text-sm font-mono overflow-auto">
                    {generatedToken.url}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(generatedToken.url, "URL")}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="default"
                    size="icon"
                    onClick={() => window.open(generatedToken.url, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-accent/10 rounded-lg">
              <p className="text-sm text-muted-foreground">
                💡 <strong>Tip:</strong> This token works exactly like a paid token but is marked as a test assessment in the database.
              </p>
            </div>
          </Card>
        )}

        <Card className="p-6 mt-8 bg-muted/50">
          <h3 className="font-semibold mb-3">Admin Features:</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>✅ Generate unlimited test tokens</li>
            <li>✅ Test complete assessment flow without payment</li>
            <li>✅ Tokens marked as "test" in database</li>
            <li>✅ No email charges for test assessments</li>
            <li>✅ All test data can be filtered/cleaned up easily</li>
          </ul>
        </Card>

        <div className="mt-12">
          <h2 className="text-3xl font-bold mb-6">Strategy Reference Resources</h2>
          <p className="text-muted-foreground mb-6">
            Upload PDF documents with gamification frameworks, case studies, or best practices. Active resources will be included in the AI's context when generating strategies.
          </p>
          <AdminResourceManager />
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
